import { AfterViewInit, Component, effect, ElementRef, inject, OnDestroy, OnInit, untracked, ViewChild } from '@angular/core';
import { OverlayRef } from '@angular/cdk/overlay';
import { SHARED_MODULES } from '../../../../shared.';
import { Element } from '../../../../equipment/models/elements/element';
import Konva from 'konva';
import { konvaSettings } from '../../../../equipment/models/konvaSettings';
import { HotkeyTooltipComponent } from '../../../../core/components/hotkey-tooltip/hotkey-tooltip.component';
import { PhIconComponent } from '../../../../core/components/ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../../../../core/components/ph-icon-wrapper/ph-icon-wrapper.component';
import { PhTooltipDirective } from '../../../../core/directives/ph-tooltip.directive';
import { EquipmentElements } from '../../../../equipment/models/elements/equipmentElements';
import { ProductGroup } from '../../../models/product-group';
import { PlanogramViewBehavior } from '../../../models/planogramViewBehavior';
import { PlanogramViewBehaviorFactory } from '../../../models/planogramViewBehaviorFactory';
import { Point } from '../../../../core/models/point';
import { Utils } from '../../../../core/services/utils.service';
import { Block } from '../../../models/block';
import { CanvasDrawerDetailedService } from '../../../../core/services/canvas-drawer-detailed.service';
import { DragOutsideService } from '../../../../core/services/drag-outside.service';
import { IPlanogram } from '../../../models/planogram';
import { ICanPutProductsResult } from '../../../models/canPutProductsResult';
import { CanvasUpdateRequest, DetailedVariables, PlanogramActivity } from '../../../services/planogram-activity.service';
import { PlanogramModes } from '../../../services/planogram-modes.service';
import { Figure } from '../../../../equipment/models/figures/figure';
import { BoxElement } from '../../../models/elements/boxElement';
import { ShelfElement } from '../../../models/elements/shelfElement';
import { HighlightProductsService } from '../../../../core/services/highlight-products.service';
import { SessionService } from '../../../../core/services/session.service';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import { PalletElement } from '../../../models/elements/palletElement';
import { HookElement } from '../../../models/elements/hookElement';
import { SnackBarService } from '../../../../core/services/snack-bar.service';
import { PlanogramTooltipService } from '../../../services/planogram-tooltip.service';
import { clampOverlayPosition, clampOverlaySizeToViewport, clampOverlayToViewport } from '../../../services/overlay-viewport.util';
import { tinykeys } from 'tinykeys';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as jsts from 'jsts/dist/jsts';
import { MeasurementSystem } from '../../../../core/models/measurementSystem';
import { UnitConverter } from '../../../../core/utils/unitConverter';
import { DETAILED_ELEMENT_WIDGET_DATA, DETAILED_ELEMENT_WIDGET_REF } from './detailed-element-widget.tokens';
import { PhSimpleZoomComponent } from '../../../../core/components/ph-simple-zoom/ph-simple-zoom.component';
import {
	ALL_PLANOGRAM_CANVAS_LAYERS,
	PlanogramCanvasLayer,
	resolvePlanogramCanvasLayers,
	shouldResizePlanogramCanvas
} from '../../../models/planogram-canvas-layer';

const Coordinate: jsts.geom.Coordinate = jsts.geom.Coordinate;
const geometryFactory: jsts.geom.GeometryFactory = new jsts.geom.GeometryFactory();

const BACK_WALLS_LIST: Array<EquipmentElements> = [
	EquipmentElements.BackWall,
	EquipmentElements.CentralPillar,
	EquipmentElements.HookWall,
	EquipmentElements.PerforatedWall,
	EquipmentElements.Showcase
];
const DRAGGABLE_ELEMENTS: { front: Array<EquipmentElements>, top: Array<EquipmentElements> } = {
	front: [
		//EquipmentElements.CentralPillar,
		EquipmentElements.Shelf,
		EquipmentElements.AngleShelf,
		EquipmentElements.HangBar,
		EquipmentElements.HookPanel,
		EquipmentElements.Hook,
		EquipmentElements.Perforation
	],
	top: [
		//EquipmentElements.CentralPillar,
		EquipmentElements.Box,
		EquipmentElements.SlicedBox,
		EquipmentElements.Partition
	]
};

interface DragState {
	dragging: boolean;
	startClientX: number;
	startClientY: number;
	startLeft: number;
	startTop: number;
	left: number;
	top: number;
	isPositionInitialized: boolean;
}

interface ResizeState {
	resizing: boolean;
	startClientX: number;
	startClientY: number;
	startWidth: number;
	startHeight: number;
	minWidth: number;
	minHeight: number;
}

enum LabelsDict {
	PROMO_LABEL = 'А',
	NEW_LABEL = 'Н'
}

@Component({
	selector: 'detailed-element-widget',
	templateUrl: './detailed-element-widget.component.html',
	styleUrls: ['./detailed-element-widget.component.scss'],
	host: {
		'[class.active]': 'canvas?.nativeElement == activity.focusedCanvas().canvas',
		'[attr.tabindex]': '0',
		'(pointerdown)': 'focusHost()'
	},
	imports: [
		SHARED_MODULES,
		DndModule,
		HotkeyTooltipComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		PhSimpleZoomComponent,
		PhTooltipDirective
	],
	providers: [CanvasDrawerDetailedService],
	standalone: true
})
export class DetailedElementWidgetComponent implements OnInit, AfterViewInit, OnDestroy {

	private readonly widgetData = inject(DETAILED_ELEMENT_WIDGET_DATA);
	private readonly widgetRef = inject(DETAILED_ELEMENT_WIDGET_REF);
	private readonly onHostClick: () => void = (): void => {
		if (!this.activity.isCanvasFocused(this.canvas.nativeElement)) {
			this.activity.setFocusedCanvas(this.planogram.main_stand, this.element.id, this.canvas.nativeElement);
			this.planogram.stand.allElements.forEach((el: Element) => {
				el.shapes.forEach((shape: Figure) => (shape as any).representation = 'top');
			});
		}
		if (this.detailedElements.every((el: Element) => el !== this.activity.targetElement())) {
			this.activity.setTargetElement(this.element);
		}
		this.activity.setPlanogram(this.planogram);
	};
	private readonly onHostWheel: (event: WheelEvent) => void = (event: WheelEvent) => {
		if (event.ctrlKey || event.metaKey) event.preventDefault();
	};
	private _element: Element;
	public planogram: IPlanogram;
	public overlayRef: OverlayRef;
	public detailedElements: Array<Element> = [];
	public detailedVariables: DetailedVariables = { detailedElementNumber: 0, detailedElementNext: null, detailedElementPrev: null };
	public showRotateCursor: boolean = false;
	public zoom: number = 100;
	private tooltipSnapshot: HTMLElement;
	private stage: Konva.Stage;
	private detailingLayer: Konva.Layer = new Konva.Layer();
	private detailingSizesLayer: Konva.Layer = new Konva.Layer();
	private blocksLayer: Konva.Layer = new Konva.Layer();
	private toolsLayer: Konva.Layer = new Konva.Layer();
	private tooltipsLayer: Konva.Layer = new Konva.Layer();
	private elementBeginPoint: { x: number; y: number } = { x: 0, y: 0 };
	private elementScale: number = 1;
	private minStep: number;
	private tinyKeysSubscription: any;
	private dragAbortController: AbortController | null = null;
	private dragState: DragState = {
		dragging: false,
		startClientX: 0,
		startClientY: 0,
		startLeft: 0,
		startTop: 0,
		left: 0,
		top: 0,
		isPositionInitialized: false
	};
	private resizeState: ResizeState = {
		resizing: false,
		startClientX: 0,
		startClientY: 0,
		startWidth: 0,
		startHeight: 0,
		minWidth: 400,
		minHeight: 200
	};
	private multiSelectConfiguration: {
		begin?: Point;
		end?: Point;
		drawing: boolean;
		ctrlKey: boolean;
	} = { drawing: false, ctrlKey: false };
	@ViewChild('canvas') canvas: ElementRef;
	@ViewChild('canvasContainer') canvasContainer: ElementRef;
	@ViewChild('headerContainer') headerContainer: ElementRef;
	@ViewChild('resizeHandle') resizeHandle: ElementRef;

	get element(): Element {
		return this._element;
	}

	set element(element: Element) {
		this._element = element;
		this.detailedElements = this.getDetailedElements(this._element);
		this.detailedVariables = this.activity.getDetailedVariables(this._element);
	}

	constructor(
		private canvasDrawerService: CanvasDrawerDetailedService,
		private dragOutsideService: DragOutsideService,
		private highlightProductsService: HighlightProductsService,
		private sessionService: SessionService,
		private snackBarService: SnackBarService,
		private planogramTooltipService: PlanogramTooltipService,
		private hostElement: ElementRef,
		public activity: PlanogramActivity,
		public modes: PlanogramModes
	) {
		this.element = this.widgetData.element;
		this.planogram = this.widgetData.planogram;
		this.overlayRef = this.widgetData.overlayRef;

		const elementMoveSteps: Map<MeasurementSystem, number> = new Map<MeasurementSystem, number>([
			[MeasurementSystem.Metrics, 0.001],
			[MeasurementSystem.Imperial, UnitConverter.getSaveConvertValue(0.01)]
		]);
		this.minStep = elementMoveSteps.get(this.sessionService.defaults.measurement_system);

		effect(() => {
			const drawBlockMode: boolean = this.modes.drawBlockMode();

			if (this.canvas) {
				this.canvas.nativeElement.style.cursor = drawBlockMode ? 'no-drop' : 'default';
				this.changeCanvasListening(!drawBlockMode);
			}
		});
		effect(() => {
			const sizesMode: boolean = this.modes.sizesMode();

			if (!this.canvasContainer) return;

			if (sizesMode) {
				this.drawSizes();
			} else {
				this.detailingSizesLayer.destroyChildren();
			}

			this.detailingSizesLayer.draw();
		});
		effect(() => {
			this.modes.imagesMode();
			this.modes.equipmentEditMode();
			this.modes.sequenceNumberMode();
			this.activity.highlightValue();
			this.activity.rotatedProducts();
			this.activity.layoutRulesProducts();

			if (!this.canvasContainer) return;
			untracked(() => {
				this.canvasResize();
				this.canvasRender();
			});
		});
		effect(() => {
			this.activity.activeProducts();

			if (!this.planogram) return;
			untracked(() => {
				this.planogram.stand.products.forEach((pg: ProductGroup) => {
					const canvasProduct: Konva.Group = this.canvasDrawerService.getCanvasDetailedProduct(pg);

					if (this.activity.isProductActive(pg)) {
						this.canvasDrawerService.setCanvasProductActive(pg, canvasProduct);
					} else {
						this.canvasDrawerService.setCanvasProductInactive(pg, canvasProduct);
					}
				});
				this.detailingLayer.batchDraw();
			});
		});
		effect(() => {
			const activeBlock: Block = this.activity.activeBlocks()[0];

			if (!this.planogram || this.element.is(HookElement)) return;

			this.planogram.stand.blocks
				.filter((block: Block) => block.elements.includes(this.element))
				.forEach((block: Block) => {
					this.canvasDrawerService.changeDetailedBlockBordersStroke(block, activeBlock);
				});

			this.detailingLayer.batchDraw();
		});
		effect(() => {
			const selectedBlock: Block = this.activity.selectedBlock();

			if (!this.planogram || this.element.is(HookElement)) return;

			this.planogram.stand.blocks
				.filter((block: Block) => block.elements.includes(this.element))
				.forEach((block: Block) => {
					if (block == selectedBlock) {
						this.canvasDrawerService.getCanvasDetailedBlockBody(block).hide();
						this.canvasDrawerService.getCanvasDetailedBlockTitle(block).hide();
					} else {
						this.canvasDrawerService.getCanvasDetailedBlockBody(block).show();
						this.canvasDrawerService.getCanvasDetailedBlockTitle(block).show();
					}
				});

			this.detailingLayer.batchDraw();
		});

		this.activity.canvasUpdate$
			.pipe(takeUntilDestroyed())
			.subscribe((req: CanvasUpdateRequest) => {
				if (req.stands?.length && !req.stands.some((stand: IPlanogram) => stand.id === this.planogram?.id)) return;

				this.canvasUpdate(req.ids ?? [], req.layers);

				if (req.requestId) {
					requestAnimationFrame(() => this.activity.canvasUpdateCompleted$.next(req));
				}
			});

		this.activity.planogramUpdate$
			.pipe(takeUntilDestroyed())
			.subscribe((planogram: IPlanogram) => {
				this.planogram = planogram;
				this.element = planogram.stand.getElementById(this.element.id);
			});

		this.dragOutsideService.drop$
			.pipe(takeUntilDestroyed())
			.subscribe((targetCanvas: HTMLElement) => {
				if (!this.canvas?.nativeElement || targetCanvas !== this.canvas.nativeElement) return;
				if (!this.activity.activeProducts().length) return;

				const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
					.createBehavior(this.planogram, this.element, this.elementScale, this.elementBeginPoint);

				this.handleProductGroupDrop(behavior, this.planogram, true);
			});

		this.dragOutsideService.redrawCanvas$
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.redrawCanvas());

		this.tinyKeysSubscription = tinykeys(this.hostElement.nativeElement, {
			'Escape': (event: KeyboardEvent) => {
				event.preventDefault();
				this.closeDetailedView();
			},
			'PageDown': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();
				this.changeDetailedElement(this.detailedVariables.detailedElementPrev);
			},
			'PageUp': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();
				this.changeDetailedElement(this.detailedVariables.detailedElementNext);
			},
			'ArrowLeft': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();
				event.stopPropagation();
				this.moveProducts(-this.minStep, 0);
			},
			'ArrowRight': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();
				event.stopPropagation();
				this.moveProducts(this.minStep, 0);
			},
			'ArrowUp': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();
				event.stopPropagation();
				this.moveProducts(0, -this.minStep);
			},
			'ArrowDown': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();
				event.stopPropagation();
				this.moveProducts(0, this.minStep);
			},
		});
	}

	public redrawCanvas(): void {
		this.canvasRender();
		this.activity.setProductMove(false);
		this.changeCanvasListening(true);
		this.clearDragOutsidePreviews();
	}

	public focusHost(): void {
		this.hostElement.nativeElement.focus();
		if (this.widgetRef.focus) this.widgetRef.focus();
	}

	public ngOnInit(): void {}

	public ngAfterViewInit(): void {
		this.focusHost();
		this.initDrag();
		this.initResize();

		this.stage = new Konva.Stage({
			container: this.canvas.nativeElement
		});
		this.stage.add(this.detailingLayer);
		this.stage.add(this.blocksLayer);
		this.stage.add(this.detailingSizesLayer);
		this.stage.add(this.tooltipsLayer);
		this.stage.add(this.toolsLayer);

		if (this.activity.editMode()) {
			const stageContent = this.stage.getContent();
			this.stage.on('pointerdown', (event: Konva.KonvaEventObject<PointerEvent>) => {
				if (!this.stage) return;
				if (this.modes.drawBlockMode() || this.modes.equipmentEditMode() || this.activity.activeBlocks().length) return;

				const eventPointerPosition = this.stage.getPointerPosition(),
					pointedShape: any = this.stage.getIntersection(eventPointerPosition);

				if (pointedShape) {
					const shapeChain = [pointedShape, pointedShape.parent, pointedShape.parent?.parent];
					const isDraggable = shapeChain.some((shape) => shape?.draggable());
					const isProductSide = shapeChain.some((shape) => shape?.name() === 'product-side');

					if (isDraggable || isProductSide) return;
				}

				this.multiSelectConfiguration.begin = eventPointerPosition;
				this.multiSelectConfiguration.end = null;
				this.multiSelectConfiguration.drawing = true;
				this.multiSelectConfiguration.ctrlKey = event.evt[navigator.platform == 'MacIntel' ? 'metaKey' : 'ctrlKey'];

				if (stageContent?.setPointerCapture) stageContent.setPointerCapture(event.evt.pointerId);
			});
			this.stage.on('pointermove', (event: Konva.KonvaEventObject<PointerEvent>) => {
				if (!this.stage) return;
				event.evt.preventDefault();

				if (
					this.multiSelectConfiguration.begin &&
					!Utils.equalObjects(this.multiSelectConfiguration.begin, this.stage.getPointerPosition()) &&
					this.multiSelectConfiguration.drawing
				)
					this.activity.clearDragProducts();

				if (
					!this.multiSelectConfiguration.drawing ||
					!this.multiSelectConfiguration.begin ||
					this.modes.drawBlockMode() ||
					this.modes.equipmentEditMode() ||
					this.activity.dragProducts().length ||
					this.activity.activeBlocks().length
				)
					return;

				const eventPointerPosition = this.stage.getPointerPosition();
				const beginPoint = this.multiSelectConfiguration.begin;

				this.toolsLayer.destroyChildren();
				this.toolsLayer.add(new Konva.Rect({
					x: Math.min(eventPointerPosition.x, beginPoint.x),
					y: Math.min(eventPointerPosition.y, beginPoint.y),
					width: Math.abs(eventPointerPosition.x - beginPoint.x),
					height: Math.abs(eventPointerPosition.y - beginPoint.y),
					stroke: '#99ddff',
					strokeWidth: 1
				}));
				this.toolsLayer.draw();

				this.multiSelectConfiguration.end = eventPointerPosition;
			});
			this.stage.on('pointerup', (event: Konva.KonvaEventObject<PointerEvent>) => {
				if (stageContent.releasePointerCapture) stageContent.releasePointerCapture(event.pointerId);

				if (this.multiSelectConfiguration.drawing) {
					this.multiSelectConfiguration.drawing = false;
					this.toolsLayer.destroyChildren();
					this.toolsLayer.draw();

					if (!this.multiSelectConfiguration.ctrlKey && this.multiSelectConfiguration.end) {
						this.activity.setProductActive(null, false, false);
					}

					if (!this.multiSelectConfiguration.begin || !this.multiSelectConfiguration.end) return;

					const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
						.createBehavior(this.planogram, this.element, this.elementScale, this.elementBeginPoint);
					const begin = behavior.convertCanvasPointToPlanogram(this.multiSelectConfiguration.begin);
					const end = behavior.convertCanvasPointToPlanogram(this.multiSelectConfiguration.end);
					const selectedArea = geometryFactory.createPolygon([
						new Coordinate(begin.x, begin.z),
						new Coordinate(begin.x, end.z),
						new Coordinate(end.x, end.z),
						new Coordinate(end.x, begin.z),
						new Coordinate(begin.x, begin.z)
					]);
					const products: Array<ProductGroup> = Utils.to1DArray([
						this.element.products,
						this.planogram.stand.allElements.filter((el: Element) => el.depend == this.element).map((el: Element) => el.products)
					]);
					const productsToAdd = products
						.filter((pg: ProductGroup) => this.planogram.stand.blocks.every((b: Block) => !b.products.includes(pg)))
						.filter((pg: ProductGroup) => selectedArea.intersects(pg.zPolygon));

					if (!productsToAdd.length) this.activity.setProductActive(null, false, false);
					productsToAdd.forEach((pg: ProductGroup) => this.activity.setProductActive(pg, true));

					this.isNeedCloseRightPanel();
				}
			});
		}

		this.activity.setFocusedCanvas(this.planogram.main_stand, this.element.id, this.canvas.nativeElement);
        this.activity.setPlanogram(this.planogram);
		this.planogram.stand.allElements.forEach((el: Element) => {
			el.shapes.forEach((shape: Figure) => (shape as any).representation = 'top');
		});
		this.hostElement.nativeElement.addEventListener('click', this.onHostClick);
		this.hostElement.nativeElement.addEventListener('wheel', this.onHostWheel, { passive: false });
		this.canvas.nativeElement.style.cursor = this.modes.drawBlockMode() ? 'no-drop' : 'default';
		this.changeCanvasListening(!this.modes.drawBlockMode());

		const sizes: { instrumentWidth: number, instrumentHeight: number } = this.getInstrumentSizes();
		this.applyInitialAutoSize(sizes.instrumentWidth, sizes.instrumentHeight);

		this.canvasResize();
		this.canvasRender();

		this.stage.draw();

		requestAnimationFrame(() => {
			if (this.overlayRef) {
				clampOverlaySizeToViewport(this.overlayRef.overlayElement as HTMLElement);
			}
		});
	}

	public applyZoom(value: number): void {
		this.zoom = value;
		this.canvasResize();
		this.canvasRender();
	}

	isNeedCloseRightPanel() {
		if (this.activity.panelType() == 'layoutrules') return;
		if (this.activity.panelType() == 'brand') return;
		if (this.activity.activeBlocks().length) return;
		if (this.activity.dragProducts().length) return;
		if (this.activity.settingsPanelDisabled()) this.activity.closePanel();
	}

	private setOverlayPosition(): DOMRect {
		if (!this.overlayRef) return;

		const overlayEl: HTMLElement = this.overlayRef!.overlayElement as HTMLElement;
		const rect: DOMRect = overlayEl.getBoundingClientRect();

		overlayEl.style.position = 'fixed';
		overlayEl.style.left = `${rect.left}px`;
		overlayEl.style.top = `${rect.top}px`;
		overlayEl.style.right = 'auto';
		overlayEl.style.bottom = 'auto';
		overlayEl.style.transform = 'none';

		return rect;
	}

	private initDrag(): void {
		if (!this.overlayRef || !this.headerContainer) return;

		if (!this.dragAbortController) this.dragAbortController = new AbortController();

		const signal: AbortSignal = this.dragAbortController.signal;

		const isInteractive = (t: HTMLElement) => {
			return !!t.closest('button,a,input,textarea,select,[role=\'button\'],[data-no-drag],.icon-wrapper');
		}

		const onPointerDown = (event: PointerEvent) => {
			const target: HTMLElement = event.target as HTMLElement;

			if (event.button !== 0 || isInteractive(target)) return;

			event.preventDefault();
			this.dragState.dragging = true;
			this.dragState.startClientX = event.clientX;
			this.dragState.startClientY = event.clientY;

			const rect: DOMRect = this.setOverlayPosition();

			this.dragState.left = rect.left;
			this.dragState.top = rect.top;

			this.dragState.startLeft = this.dragState.left;
			this.dragState.startTop = this.dragState.top;

			this.headerContainer.nativeElement.setPointerCapture?.(event.pointerId);
		};

		const onPointerMove = (event: PointerEvent) => {
			if (!this.dragState.dragging || !this.overlayRef) return;

			const overlayEl: HTMLElement = this.overlayRef.overlayElement as HTMLElement;
			const dx: number = event.clientX - this.dragState.startClientX;
			const dy: number = event.clientY - this.dragState.startClientY;
			const nextLeft: number = this.dragState.startLeft + dx;
			const nextTop: number = this.dragState.startTop + dy;
			const clamped = clampOverlayPosition(nextLeft, nextTop, overlayEl);

			this.dragState.left = clamped.left;
			this.dragState.top = clamped.top;

			overlayEl.style.left = `${clamped.left}px`;
			overlayEl.style.top = `${clamped.top}px`;
		};

		const onPointerUp = (event: PointerEvent) => {
			this.dragState.dragging = false;
			this.headerContainer.nativeElement.releasePointerCapture?.(event.pointerId);

			if (this.overlayRef) {
				clampOverlayToViewport(this.overlayRef.overlayElement as HTMLElement);
			}
		};

		this.headerContainer.nativeElement.style.touchAction = 'none';
		this.headerContainer.nativeElement.addEventListener('pointerdown', onPointerDown, { signal });
		this.headerContainer.nativeElement.addEventListener('pointermove', onPointerMove, { signal });
		this.headerContainer.nativeElement.addEventListener('pointerup', onPointerUp, { signal });
		this.headerContainer.nativeElement.addEventListener('pointercancel', onPointerUp, { signal });
	}

	private initResize(): void {
		if (!this.overlayRef || !this.resizeHandle) return;

		if (!this.dragAbortController) this.dragAbortController = new AbortController();

		const signal: AbortSignal = this.dragAbortController.signal;
		let canvasTimer: number = null;
		let frame: number = null;
		let lastX: number = 0;
		let lastY: number = 0;

		const requestCanvasUpdate = () => {
			if (canvasTimer != null) return;
			canvasTimer = window.setTimeout(() => {
				canvasTimer = null;
				if (this.canvasContainer) {
					this.canvasResize();
					this.canvasRender();
				}
			}, 100);
		};

		const onPointerDown = (event: PointerEvent) => {
			if (event.button !== 0 || !event.isPrimary) return;

			event.preventDefault();

			const rect: DOMRect = this.setOverlayPosition();

			lastX = event.clientX;
			lastY = event.clientY;

			this.resizeState.resizing = true;
			this.resizeState.startClientX = event.clientX;
			this.resizeState.startClientY = event.clientY;
			this.resizeState.startWidth = rect.width;
			this.resizeState.startHeight = rect.height;

			this.resizeHandle.nativeElement.setPointerCapture?.(event.pointerId);
		};

		const onPointerMove = (event: PointerEvent) => {
			if (!this.resizeState.resizing || !this.overlayRef || !event.isPrimary) return;

			lastX = event.clientX;
			lastY = event.clientY;

			if (frame == null) {
				frame = requestAnimationFrame(() => {
					frame = null;
					const overlayEl: HTMLElement = this.overlayRef.overlayElement as HTMLElement;
					const dx: number = lastX - this.resizeState.startClientX;
					const dy: number = lastY - this.resizeState.startClientY;

					const nextWidth: number = Math.max(this.resizeState.minWidth, this.resizeState.startWidth + dx);
					const nextHeight: number = Math.max(this.resizeState.minHeight, this.resizeState.startHeight + dy);

					overlayEl.style.width = `${nextWidth}px`;
					overlayEl.style.height = `${nextHeight}px`;
					clampOverlaySizeToViewport(overlayEl);

					requestCanvasUpdate();
				});
			}
		};

		const onPointerUp = (event: PointerEvent) => {
			if (!event.isPrimary) return;

			this.resizeState.resizing = false;

			if (this.resizeHandle.nativeElement.hasPointerCapture?.(event.pointerId)) {
				this.resizeHandle.nativeElement.releasePointerCapture?.(event.pointerId);
			}

			if (canvasTimer != null) {
				clearTimeout(canvasTimer);
				canvasTimer = null;
			}

			if (this.canvasContainer) {
				this.canvasResize();
				this.canvasRender();
			}

			if (frame != null) {
				cancelAnimationFrame(frame);
				frame = null;
			}
		};

		this.resizeHandle.nativeElement.style.touchAction = 'none';
		this.resizeHandle.nativeElement.addEventListener('pointerdown', onPointerDown, { signal });
		this.resizeHandle.nativeElement.addEventListener('pointermove', onPointerMove, { signal });
		this.resizeHandle.nativeElement.addEventListener('pointerup', onPointerUp, { signal });
		this.resizeHandle.nativeElement.addEventListener('pointercancel', onPointerUp, { signal });
	}

	private applyInitialAutoSize(instrumentWidth: number, instrumentHeight: number): void {
		if (!this.overlayRef || !this.canvasContainer || instrumentWidth <= 0 || instrumentHeight <= 0) return;

		if (this.widgetData.boundingRect) {
			const overlayEl: HTMLElement = this.overlayRef.overlayElement as HTMLElement;

			overlayEl.style.width = `${this.widgetData.boundingRect.width}px`;
			overlayEl.style.height = `${this.widgetData.boundingRect.height}px`;
			overlayEl.style.position = `fixed`;
		} else {
			const overlayEl: HTMLElement = this.overlayRef.overlayElement as HTMLElement;
			const minOverlayWidth: number = Math.min(document.body.clientWidth, instrumentWidth * 700);
			const currentWidth: number = Math.ceil(overlayEl.getBoundingClientRect().width || minOverlayWidth);
			const nextWidth: number = Math.max(minOverlayWidth, currentWidth);

			const scaleFromWidth: number = (nextWidth - konvaSettings.detailedCanvasOffset * 2) / instrumentWidth;
			const contentHeight: number = scaleFromWidth * instrumentHeight + konvaSettings.detailedCanvasOffset * 2;
			const nextHeight: number = Math.min(
				document.body.clientHeight,
				Math.max(this.resizeState.minHeight, Math.ceil(contentHeight + (this.headerContainer?.nativeElement?.offsetHeight ?? 0)))
			);

			overlayEl.style.width = `${nextWidth}px`;
			overlayEl.style.height = `${nextHeight}px`;
		}
	}

	private getDetailedElements(element: Element): Array<Element> {
		const detailedElements: Array<Element> = [element];

		if (element.depend?.parent.is(...BACK_WALLS_LIST)) {
			if (element.depend.getBoundingY().max > element.getBoundingY().max) {
				detailedElements.push(element.depend);
			}
		}

		detailedElements.push(
			...(element.stand as any).orderedElements.filter((el: Element) => el.depend == element),
			...element.stand.getVerticalIntersectedElements(element)
		);

		return detailedElements;
	}

	private canvasClear(
		elements: Array<string> = [],
		layers: Array<PlanogramCanvasLayer> = [...ALL_PLANOGRAM_CANVAS_LAYERS]
	): void {
		const layerSet: Set<PlanogramCanvasLayer> = new Set(layers);

		if (this.tooltipSnapshot) {
			this.tooltipSnapshot.remove();
			this.tooltipSnapshot = null;
		}

		if (layerSet.has(PlanogramCanvasLayer.Planogram)) {
			if (!elements.length) {
				this.detailingLayer.destroyChildren();
			} else {
				this.detailingLayer.getChildren().forEach((el: Konva.Group) => {
					if (el.id() !== '' && elements.includes(el.id())) {
						el.destroyChildren();
					}
				});
			}
		}

		if (layerSet.has(PlanogramCanvasLayer.Tooltips)) {
			if (!elements.length) {
				this.tooltipsLayer.destroyChildren();
			} else {
				this.tooltipsLayer.getChildren().forEach((el: Konva.Group) => {
					if (el.id() !== '' && elements.includes(el.id())) {
						el.destroyChildren();
					}
				});
			}
		}

		if (layerSet.has(PlanogramCanvasLayer.Blocks)) {
			this.blocksLayer.destroyChildren();
		}

		if (layerSet.has(PlanogramCanvasLayer.Tools)) {
			this.toolsLayer.destroyChildren();
			this.toolsLayer.draw();
		}

		if (layerSet.has(PlanogramCanvasLayer.Sizes)) {
			this.detailingSizesLayer.destroyChildren();
		}
	}

	private canvasUpdate(elements: Array<string> = [], layers?: Array<PlanogramCanvasLayer>): void {
		if (!this.canvasContainer) return;

		const layersToRender: Array<PlanogramCanvasLayer> = resolvePlanogramCanvasLayers(layers);
		let paintElements: Array<string> = elements;
		let finalLayers: Array<PlanogramCanvasLayer> = layersToRender;

		if (shouldResizePlanogramCanvas(layers)) {
			this.canvasResize();
			paintElements = [];
			finalLayers = [...ALL_PLANOGRAM_CANVAS_LAYERS];
		}

		this.canvasRender(paintElements, finalLayers);
	}

	private canvasRender(
		elements: Array<string> = [],
		layers: Array<PlanogramCanvasLayer> = [...ALL_PLANOGRAM_CANVAS_LAYERS]
	): void {
		const layerSet: Set<PlanogramCanvasLayer> = new Set(layers);

		this.canvasClear(elements, layers);

		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(this.planogram, this.element, this.elementScale, this.elementBeginPoint);
		const offset: Point = behavior.getOffset();
		if (this.element.parent.is(EquipmentElements.Hook)) offset.y -= 0.025;

		if (layerSet.has(PlanogramCanvasLayer.Planogram)) {
			this.detailedElements.forEach((el: Element) => this.drawDetailedElement(el, offset));

			const detailedProducts: Array<ProductGroup> = this.planogram.stand.products
				.filter((pg: ProductGroup) => this.detailedElements.includes(pg.element));

			detailedProducts.forEach((pg: ProductGroup) => this.drawDetailedProduct(this.planogram, pg, offset));

			detailedProducts
				.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
				.sort((a: ProductGroup, b: ProductGroup) => b.begin.z - a.begin.z)
				.forEach((pg: ProductGroup) => this.canvasDrawerService.getCanvasDetailedProduct(pg).moveToTop());
		}

		if (layerSet.has(PlanogramCanvasLayer.Blocks) && (this.activity.editMode() || this.activity.labelsState().blocks)) {
			this.drawDetailedBlocks(offset);
		}

		if (
			layerSet.has(PlanogramCanvasLayer.Tools)
			&& this.modes.equipmentEditMode()
			&& this.activity.shouldShowEquipmentDistanceLines(this.planogram.main_stand)
		) {
			this.drawDistanceLines(this.planogram.stand.getElementById(this.activity.selectedElement()), offset);
		}

		if (layerSet.has(PlanogramCanvasLayer.Sizes) && this.modes.sizesMode()) {
			this.drawSizes();
		}
	}

	private canvasResize(): void {
		const { instrumentWidth, instrumentHeight } = this.getInstrumentSizes();

		const containerWidth: number = this.canvasContainer.nativeElement.clientWidth;
		const containerHeight: number = this.canvasContainer.nativeElement.clientHeight;

		const workingWidth: number = containerWidth - konvaSettings.detailedCanvasOffset * 2;
		const workingHeight: number = containerHeight - konvaSettings.detailedCanvasOffset * 2;

		if (instrumentWidth / instrumentHeight > workingWidth / workingHeight) {
			this.elementScale = Math.floor(((workingWidth / instrumentWidth) * this.zoom) / 100);
		} else {
			this.elementScale = Math.floor(((workingHeight / instrumentHeight) * this.zoom) / 100);
		}

		const width: number = this.elementScale * instrumentWidth + konvaSettings.detailedCanvasOffset * 2;
		const height: number = this.elementScale * instrumentHeight + konvaSettings.detailedCanvasOffset * 2;

		this.elementBeginPoint.x = 0;
		this.elementBeginPoint.y = 0;

		if (containerWidth > width) this.elementBeginPoint.x += Math.ceil((containerWidth - width) / 2);
		if (containerHeight > height) this.elementBeginPoint.y += Math.ceil((containerHeight - height) / 2);

		this.canvas.nativeElement.style.width = `${containerWidth > width ? containerWidth : width}px`;
		this.canvas.nativeElement.style.height = `${containerHeight > height ? containerHeight : height}px`;

		this.stage.width(containerWidth > width ? containerWidth : width);
		this.stage.height(containerHeight > height ? containerHeight : height);
	}

	private getInstrumentSizes(): { instrumentWidth: number; instrumentHeight: number } {
		let instrumentWidth: number, instrumentHeight: number;
		const elementProducts: Array<ProductGroup> = this.element.products.flat();

		if (this.element.parent.is(EquipmentElements.Hook)) {
			const minHookHeight: number = 0.2;
			const hangerHeight: number = this.element.depend.parent.is(EquipmentElements.HangBar) ? 0.08 : 0;
			const maxProductHeight: number = Math.max(
				...elementProducts.map((pg: ProductGroup) => pg.Height + hangerHeight),
				minHookHeight
			);
			const minZ: number = Math.min(
				...elementProducts.map((pg: ProductGroup) => pg.getBoundingZ().min),
				this.element.getBoundingZ().min
			);
			const maxZ: number = Math.max(
				...elementProducts.map((pg: ProductGroup) => pg.getBoundingZ().max),
				this.element.getBoundingZ().max
			);
			instrumentWidth = Utils.round(maxZ - minZ, 3);
			instrumentHeight = this.element.Height + maxProductHeight + 0.05;
		} else {
			const minZ: number = Math.min(
				...elementProducts.map((pg: ProductGroup) => pg.getBoundingZ().min),
				...this.detailedElements.map((el: Element) => el.getBoundingZ().min)
			);
			const maxZ: number = Math.max(
				...elementProducts.map((pg: ProductGroup) => pg.getBoundingZ().max),
				...this.detailedElements.map((el: Element) => el.getBoundingZ().max)
			);
			const minX: number = Math.min(
				...elementProducts.map((pg: ProductGroup) => pg.getBoundingX().min),
				...this.detailedElements.map((el: Element) => el.getBoundingX().min)
			);
			const maxX: number = Math.max(
				...elementProducts.map((pg: ProductGroup) => pg.getBoundingX().max),
				...this.detailedElements.map((el: Element) => el.getBoundingX().max)
			);
			instrumentWidth = Utils.round(maxX - minX, 3);
			instrumentHeight = Utils.round(maxZ - minZ, 3);
		}

		return { instrumentWidth, instrumentHeight };
	}

	private drawDetailedElement(element: Element, normalize: Point) {
		const canvasElement: Konva.Group = this.canvasDrawerService.createCanvasDetailedEquipmentElement({
			element: element,
			beginPoint: this.elementBeginPoint,
			scale: this.elementScale,
			zoom: this.zoom,
			normalize: normalize,
			active: false
		}, element.stand);

		if (this.activity.targetElement() == element) {
			this.canvasDrawerService.setEquipmentElementHovered(element);
		} else {
			this.canvasDrawerService.setEquipmentElementInactive(element);
		}

		if (element.is(BoxElement, ShelfElement)) {
			canvasElement.off('click');
			canvasElement.on('click', () => {
				if (this.activity.targetElement()) {
					this.canvasDrawerService.setEquipmentElementInactive(this.activity.targetElement());
				}
				this.activity.setTargetElement(element);
				this.canvasDrawerService.setEquipmentElementHovered(element);
				this.detailingLayer.batchDraw();
				if (this.modes.equipmentEditMode()) {
					if (element.parent.is(...DRAGGABLE_ELEMENTS.top)) {
						this.activity.setFocusedCanvas(this.planogram.main_stand, element.id, this.canvas.nativeElement);
						this.activity.setSelectedElement(element.id);
						this.drawDistanceLines(element, normalize);
					} else {
						this.activity.clearSelectedElement();
						this.drawDistanceLines();
					}
				}
			});
		}

		if (this.activity.editMode() && this.modes.equipmentEditMode() && element.parent.is(EquipmentElements.Partition)) {
			canvasElement.off('click');
			canvasElement.on('click', () => {
				this.activity.setFocusedCanvas(this.planogram.main_stand, element.id, this.canvas.nativeElement);
				this.activity.setSelectedElement(element.id);
				this.drawDistanceLines(element, normalize);
			});
		}

		if (this.activity.editMode() && this.modes.equipmentEditMode() && element.parent.is(...DRAGGABLE_ELEMENTS.top)) {
			let relatedProducts: Array<ProductGroup> = [];
			let xMovement: number = 0;
			let zMovement: number = 0;
			let prevX: number = 0;
			let prevZ: number = 0;

			canvasElement.draggable(true);
			canvasElement.off('dragstart dragmove dragend');
			canvasElement.on('dragstart', () => {
				if (!element.parent.is(EquipmentElements.Partition)) {
					if (this.activity.targetElement()) {
						this.canvasDrawerService.setEquipmentElementInactive(this.activity.targetElement());
					}
					this.activity.setTargetElement(element);
					this.canvasDrawerService.setEquipmentElementHovered(element);
					this.detailingLayer.batchDraw();
				}
				relatedProducts = element.products.flat();
				prevX = canvasElement.x();
				prevZ = canvasElement.y();
				this.activity.setFocusedCanvas(this.planogram.main_stand, element.id, this.canvas.nativeElement);
				this.activity.setSelectedElement(element.id);
				this.drawDistanceLines(element, normalize);
			});
			canvasElement.on('dragmove', () => {
				const dependElement: Konva.Group = this.canvasDrawerService.getCanvasDetailedEquipmentElement(element.depend);

				if (canvasElement.x() < dependElement.x()) canvasElement.x(dependElement.x());
				if (canvasElement.x() + canvasElement.width() > dependElement.x() + dependElement.width()) {
					canvasElement.x(dependElement.x() + dependElement.width() - canvasElement.width());
				}
				if (element.parent.is(EquipmentElements.CentralPillar)) {
					canvasElement.y(prevZ);
				} else {
					if (canvasElement.y() < dependElement.y()) canvasElement.y(dependElement.y());
					if (canvasElement.y() + canvasElement.height() > dependElement.y() + dependElement.height()) {
						canvasElement.y(dependElement.y() + dependElement.height() - canvasElement.height());
					}
				}

				relatedProducts.forEach((pg: ProductGroup) => {
					const productGroup: Konva.Group = this.canvasDrawerService.getCanvasDetailedProduct(pg);

					productGroup.offsetX(prevX - canvasElement.x());
					productGroup.offsetY(prevZ - canvasElement.y());
				});
				this.drawDistanceLines(element, normalize, { x: -prevX + canvasElement.x(), y: -prevZ + canvasElement.y() });
				this.detailingLayer.batchDraw();
			});
			canvasElement.on('dragend', () => {
				const dependElement: Konva.Group = this.canvasDrawerService.getCanvasDetailedEquipmentElement(element.depend);

				if (canvasElement.x() < dependElement.x()) canvasElement.x(dependElement.x());
				if (canvasElement.x() + canvasElement.width() > dependElement.x() + dependElement.width()) {
					canvasElement.x(dependElement.x() + dependElement.width() - canvasElement.width());
				}
				if (element.parent.is(EquipmentElements.CentralPillar)) {
					canvasElement.y(prevZ);
				} else {
					if (canvasElement.y() < dependElement.y()) canvasElement.y(dependElement.y());
					if (canvasElement.y() + canvasElement.height() > dependElement.y() + dependElement.height()) {
						canvasElement.y(dependElement.y() + dependElement.height() - canvasElement.height());
					}
				}

				xMovement = Utils.round((canvasElement.x() - prevX) / this.elementScale);
				zMovement = Utils.round((canvasElement.y() - prevZ) / this.elementScale);

				element.shapes[0].drag(xMovement, null, zMovement);

				relatedProducts.forEach((pg: ProductGroup) => pg.move(xMovement, null, zMovement));
				element.stand.blocks.forEach((block: Block) => block.clearCache());
				const elements: Array<Element> = this.activity.normalizeElements(element.stand);

				this.activity.finalizeSharedEquipmentMove(
					this.planogram,
					elements.map((el: Element) => el.id)
				);
			});
		}

		this.detailingLayer.add(canvasElement);
	}

	private drawDetailedBlocks(normalize: Point) {
		if (!this.element.is(ShelfElement, PalletElement)) return;

		this.planogram.stand.blocks
			.filter((b: Block) => b.elements.includes(this.element))
			.forEach((b: Block) => {
				const canvasDetailedBlock: Konva.Group = this.canvasDrawerService.createCanvasDetailedBlock({
					block: b,
					detailedElement: this.element,
					beginPoint: this.elementBeginPoint,
					scale: this.elementScale,
					active: this.activity.isBlockActive(b),
					selected: this.activity.selectedBlock() == b,
					normalize: normalize,
					draggable: false,
					zoom: this.zoom
				});
				const blockRect: Konva.Rect = this.canvasDrawerService.getCanvasDetailedBlockBody(b);
				const blockDescriptionIcon: Konva.Image = this.canvasDrawerService.getCanvasDetailedBlockDescriptionIcon(b);
				const blockName: Konva.Text = this.canvasDrawerService.getCanvasDetailedBlockName(b);

				if (this.activity.editMode()) {
					blockRect.on('click', () => {
						const prevActiveBlock: Block = this.activity.activeBlocks()[0];
						this.activity.clearSelectedBlock();
						this.activity.setBlockActive(b);
						if (prevActiveBlock !== b) this.activity.rebuildProductsTable$.next();
					});

					blockRect.on('dblclick', () => {
						this.activity.setBlockActive();
						this.activity.setSelectedBlock(b);
					});
				} else {
					blockRect.hide();
				}

				if (this.canvasDrawerService.getCanvasDetailedBlockTooltipNecessity(b)) {
					const tooltip = this.canvasDrawerService.createCanvasDetailedBlockTooltip(b, b.name, this.canvas.nativeElement);
					this.tooltipsLayer.add(tooltip);
					blockName.listening(true);

					blockName.on('mouseenter', () => {
						tooltip.show();
						this.tooltipsLayer.draw();
					});

					blockName.on('mouseleave', () => {
						tooltip.hide();
						this.tooltipsLayer.draw();
					});
				}

				if (b.description) {
					const descriptionTooltip = this.canvasDrawerService.createCanvasDetailedBlockDescriptionTooltip(
						b,
						b.description,
						this.canvas.nativeElement
					);
					this.tooltipsLayer.add(descriptionTooltip);
					blockDescriptionIcon.listening(true);

					blockDescriptionIcon.on('mouseenter', () => {
						descriptionTooltip.show();
						this.tooltipsLayer.draw();
					});

					blockDescriptionIcon.on('mouseleave', () => {
						descriptionTooltip.hide();
						this.tooltipsLayer.draw();
					});
				}

				this.blocksLayer.add(canvasDetailedBlock);
			});
	}

	private drawDetailedProduct(stand, product: ProductGroup, normalize) {
		let dataObject: any = {};
		const standData = this.activity.getStandData(stand.id);
		if (standData && standData.hasOwnProperty(product.product.id)) {
			dataObject = Object.assign(dataObject, standData[product.product.id]);
		}
		if (this.activity.isProductHighlighted(product.product.id)) {
			dataObject = this.highlightProductsService.getCorrectHighlight(
				this.activity.highlightValue(),
				this.activity.highlightedProducts(),
				this.planogram.stand.products,
				product
			);
		} else if (this.activity.isProductRotated(product.product.id)) {
			dataObject = this.activity.rotatedProducts()[product.product.id];
		} else if (this.activity.isProductInRule(product.product.id)) {
			const object = this.activity.layoutRulesProducts()[product.product.id],
				property = product.element.is(ShelfElement, PalletElement)
					? Utils.getElementIndex(product.element, stand.stand).toString()
					: 'default';
			dataObject = object[property] || object['default'] || {};
		}

		const canvasDetailedProduct: Konva.Group = this.canvasDrawerService.createCanvasDetailedProduct({
			productGroup: product,
			stand: stand.stand,
			scale: this.elementScale,
			beginPoint: this.elementBeginPoint,
			normalize: normalize,
			articleMode: !this.modes.imagesMode(),
			sequenceNumberMode: this.modes.sequenceNumberMode(),
			dataObject: dataObject,
			showStockQty: this.activity.labelsState().stockQty,
			showPrices: this.activity.labelsState().price,
			showAbcTurnover: this.activity.labelsState().abcTurnover,
			showAbcReceipts: this.activity.labelsState().abcReceipts,
			zoom: this.zoom,
			callback: () => {
				if (this.activity.activeProducts().includes(product)) {
					this.canvasDrawerService.setCanvasProductActive(product, canvasDetailedProduct);
				}
				this.detailingLayer.draw();
			}
		});

		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(this.planogram, this.element, this.elementScale, this.elementBeginPoint);

		if (this.activity.editMode() && !this.modes.equipmentEditMode() && product.element.canRotateProductGroup(product)) {
			const rotationCircle = this.canvasDrawerService.createProductRotationCircle(
				canvasDetailedProduct,
				{ zoom: this.zoom }
			);
			canvasDetailedProduct.add(rotationCircle);
			rotationCircle.off('mouseenter mouseleave dragmove dragend');

			rotationCircle.on('mouseenter', () => this.showRotateCursor = true);

			rotationCircle.on('mouseleave', () => this.showRotateCursor = false);

			rotationCircle.on('dragmove', (event: Konva.KonvaEventObject<MouseEvent>) => {
				this.activity.setProductMove(true);
				this.showRotateCursor = true;
				const x: number = event.evt.offsetX;
				const y: number = event.evt.offsetY;
				const rotationXPoint: number = (product.detailedRotationPoint.x - normalize.x) * this.elementScale + konvaSettings.detailedCanvasOffset + this.elementBeginPoint.x;
				const rotationYPoint: number = (product.detailedRotationPoint.y - normalize.y) * this.elementScale + konvaSettings.detailedCanvasOffset + this.elementBeginPoint.y;
				const radians: number = Math.atan2(x - rotationXPoint, y - rotationYPoint);
				let rotationDeltaAngle: number = 180;
				let sizesProportion: number = canvasDetailedProduct.width() / canvasDetailedProduct.height();
				if (this.sessionService.personalSettings.productRotationPoint === 'topLeft') {
					rotationDeltaAngle = 90;
					sizesProportion = canvasDetailedProduct.height() / canvasDetailedProduct.width();
				}
				let angle: number = Math.round(radians * (180 / Math.PI) * -1 + rotationDeltaAngle);
				angle -= Math.round(Math.atan(sizesProportion) * 180 / Math.PI);

				// the point where the product would start if the rotation angle were 0
				const baseCorePoint: [number, number] = Utils.rotatePoint(
					[rotationXPoint, rotationYPoint],
					[canvasDetailedProduct.x(), canvasDetailedProduct.y()],
					-canvasDetailedProduct.rotation()
				);

				// the point where the product will start after changing the angle of rotation
				const newCorePoint: [number, number] = Utils.rotatePoint(
					[rotationXPoint, rotationYPoint],
					baseCorePoint,
					angle
				);

				canvasDetailedProduct.setAttrs({
					x: newCorePoint[0],
					y: newCorePoint[1]
				});
				canvasDetailedProduct.rotation(angle);

				rotationCircle.setAttrs({
					x: canvasDetailedProduct.width(),
					y: this.sessionService.personalSettings.productRotationPoint === 'topLeft'
						? canvasDetailedProduct.height()
						: 0
				});
			});

			rotationCircle.on('dragend', () => {
				this.showRotateCursor = false;
				let angle: number = canvasDetailedProduct.rotation();
				if (product.angle === angle) return;
				product.angle = angle;
				const element: Element = behavior.getAnchorElement(product, product.begin);
				if (element) {
					const oldBegin: Point = { ...product.begin };
					product.Element = element;
					product.begin = oldBegin;
				}

				if (!this.activity.activeProducts().length) {
					return this.canvasRender();
				}

				const stand = this.activity.activeProducts()[0].element.stand,
					elementsToNormalize = Utils.unique(
						stand.products
							.filter((pg) =>
								this.activity.activeProducts().some((p) => pg.product.id == p.product.id)
							)
							.map((pg) => pg.element)
					);

				const elements: Array<Element> = this.activity.normalizeElements(stand, elementsToNormalize);

				this.planogram.stand.blocks.forEach((b) => {
					const blockProductErrors = b.setProducts(
						Utils.to1DArray(
							this.planogram.stand.blocks.filter((block) => block != b).map((block) => block.products)
						)
					);

					if (blockProductErrors.length) this.activity.showDeleteMessage$.next({ block: b, errors: blockProductErrors });
				});

				this.activity.refreshActiveProducts();
				this.activity.setProductMove(false);
				this.activity.historyGenerate$.next();
				this.activity.requestCanvasUpdate([this.planogram], elements.map((el: Element) => el.id));
			});
		}

		canvasDetailedProduct.off('click dblclick dragstart dragmove dragend mouseenter mouseleave');

		if (this.activity.editMode()) {
			let prevX: number = canvasDetailedProduct.x();
			let prevY: number = canvasDetailedProduct.y();

			canvasDetailedProduct.draggable(!this.modes.equipmentEditMode());

			canvasDetailedProduct.on('click', (event) => {
				this.activity.setProductActive(product, navigator.platform == 'MacIntel' ? event.evt.metaKey : event.evt.ctrlKey);
				this.activity.syncProducts(product, event.evt);
			});

			canvasDetailedProduct.on('dblclick', () => this.activity.changeProductPosition(product));

			canvasDetailedProduct.on('dragstart', (event) => {
				if (event.target.name() === 'product-rotation-circle') return;
				this.activity.retainActiveProductsForStand(this.planogram.stand);
				this.activity.setProductMove(true);
				this.clearDragOutsidePreviews();

				let dependedProducts = [];

				if (!this.activity.isProductActive(product)) this.activity.clearActiveProducts();

				if (this.activity.activeProducts().length) {
					[...this.activity.activeProducts().filter((el) => el !== product), product].forEach((el) => {
						if (this.activity.isProductActive(el.dependProduct)) return;
						if (el.element.parent.type != 'Hook') {
							dependedProducts = [
								...dependedProducts,
								...Utils.findDependProducts(el, this.planogram.stand.products)
							];
						}
					});
				} else {
					this.activity.clearActiveProducts();

					dependedProducts = product.element.parent.type == 'Hook'
						? []
						: Utils.findDependProducts(product, this.planogram.stand.products);
				}

				if (this.tooltipSnapshot) {
					this.tooltipSnapshot.remove();
					this.tooltipSnapshot = null;
				}
				this.canvasDrawerService.getCanvasDetailedProductTooltip(product).hide();
				this.tooltipsLayer.batchDraw();

				if (dependedProducts.length) {
					[product, ...dependedProducts]
						.filter((pg) => !this.activity.isProductActive(pg))
						.forEach((pg) => this.activity.setProductActive(pg, true));
				} else {
					if (!this.activity.isProductActive(product)) {
						this.activity.setProductActive(product, true);
						this.canvasDrawerService.setCanvasProductActive(product, canvasDetailedProduct);
					}
				}

				this.activity.activeProducts()
					.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingY().max - b.getBoundingY().max)
					.map((pg: ProductGroup) => this.canvasDrawerService.getCanvasDetailedProduct(pg))
					.forEach((canvasProduct: Konva.Group) => {
						canvasProduct.moveToTop();
						canvasProduct.find('.hanger').forEach((canvasHanger: Konva.Group) => canvasHanger.hide());
					});

				this.activity.syncProducts(product, event.evt);
				prevX = canvasDetailedProduct.x();
				prevY = canvasDetailedProduct.y();
			});

			canvasDetailedProduct.on('dragmove', (event) => {
				if (event.target.name() === 'product-rotation-circle') return;
				const diffX: number = canvasDetailedProduct.x() - prevX, diffY: number = canvasDetailedProduct.y() - prevY;

				this.activity.activeProducts()
					.filter((pg: ProductGroup) => pg != product)
					.forEach((pg: ProductGroup) => {
						const konvaEl: Konva.Group = this.canvasDrawerService.getCanvasDetailedProduct(pg);
						konvaEl.x(konvaEl.x() + diffX);
						konvaEl.y(konvaEl.y() + diffY);
					});

				prevX = canvasDetailedProduct.x();
				prevY = canvasDetailedProduct.y();
				this.updateDragOutsidePreviews();
			});

			canvasDetailedProduct.on('dragend', (event) => {
				if (event.target.name() === 'product-rotation-circle') return;
				const sourceCanvas = this.canvas?.nativeElement;
				const dropCanvases = DragOutsideService.getDropTargetCanvases();
				const targetCanvas = sourceCanvas
					? this.dragOutsideService.emitDropIfTargetDifferent(this.activity.activeProducts(), dropCanvases, sourceCanvas)
					: null;
				if (targetCanvas) return this.redrawCanvas();
				this.handleProductGroupDrop(behavior, stand);
			});
		}

		if (this.activity.editMode()) {
			this.planogramTooltipService
				.buildTooltipText(
					product,
					dataObject,
					this.sessionService.personalSettings.planogramEditTooltipFields,
					{
						abcTurnover: this.activity.labelsState().abcTurnover,
						abcReceipts: this.activity.labelsState().abcReceipts,
						multiplePlanograms: this.activity.allPlanograms().length > 1,
					}
				)
				.subscribe((tooltip: string) => {
					const tooltipLabel: Konva.Label = this.canvasDrawerService.createCanvasDetailedProductTooltip(
						product,
						tooltip,
						this.canvas.nativeElement
					);

					this.tooltipsLayer.add(tooltipLabel);
				});
		} else {
			this.planogramTooltipService
				.buildViewTooltipText(
					product,
					dataObject,
					this.activity.filterType(),
					{ interval: this.activity.filters().interval, metrics: this.activity.filters().metrics }
				)
				.subscribe((tooltip: string) => {
					const tooltipLabel: Konva.Label = this.canvasDrawerService.createCanvasDetailedProductTooltip(
						product,
						tooltip,
						this.canvas.nativeElement
					);

					this.tooltipsLayer.add(tooltipLabel);
				});
		}

		canvasDetailedProduct.on('mouseenter', (event) => {
			if (event.target.name() === 'product-rotation-circle') return;

			const tooltipLabel = this.canvasDrawerService.getCanvasDetailedProductTooltip(product);
			tooltipLabel.show();
			this.tooltipsLayer.draw();

			const tooltipRect = tooltipLabel.getClientRect();
			const dimensions: Array<boolean> = [
				tooltipRect.x < 0,
				tooltipRect.y < 0,
				tooltipRect.x + tooltipRect.width > this.canvas.nativeElement.clientWidth,
				tooltipRect.y + tooltipRect.height > this.canvas.nativeElement.clientHeight,
			];

			if (dimensions.some((item: boolean) => item)) {
				this.tooltipSnapshot = this.createTooltipSnapshot(tooltipLabel, tooltipRect);
			}
		});
		canvasDetailedProduct.on('mouseleave', () => {
			this.canvasDrawerService.getCanvasDetailedProductTooltip(product).hide();
			this.tooltipsLayer.draw();
			if (this.tooltipSnapshot) {
				this.tooltipSnapshot.remove();
				this.tooltipSnapshot = null;
			}
		});

		if ((this.activity.labelsState().articles || dataObject.new_product || dataObject.promo_product) &&
			!stand.stand.products
				.filter((productGroup: ProductGroup) => !productGroup.element.is(HookElement))
				.filter((productGroup: ProductGroup) =>
					productGroup.product.id !== product.product.id &&
					productGroup.element.idx === product.element.idx &&
					productGroup.begin.y > product.begin.y &&
					productGroup.begin.x === product.begin.x
				)
				.length
		) {
			let labelText = '', labelFill = 'white';
			if (dataObject.promo_product) {
				labelText = LabelsDict.PROMO_LABEL;
				labelFill = 'red';
			}
			if (dataObject.new_product) {
				labelText = LabelsDict.NEW_LABEL;
				labelFill = '#ECC532';
			}
			if (this.activity.labelsState().articles) {
				labelText = String(product.product.article);
				labelFill = 'white';
			}
			const labelGroup = this.canvasDrawerService.drawTextLabel({
				productGroup: product,
				stand: stand.stand,
				scale: stand.detalisationScale,
				beginPoint: stand.beginDetailPoint,
				zoom: this.zoom
			}, canvasDetailedProduct, 'label', labelText, labelFill, product.angle > 90 && product.angle < 270);

			labelGroup.setAttrs({
				x: canvasDetailedProduct.width() / 2 - labelGroup.width() / 2,
				y: canvasDetailedProduct.height()
			});
		}

		this.detailingLayer.add(canvasDetailedProduct);

		if (this.activity.isProductActive(product)) {
			this.canvasDrawerService.setCanvasProductActive(product, canvasDetailedProduct);
		}

		this.activity.setProductMove(false);
	}

	private drawDistanceLines(element: Element = null, normalize: Point = { x: 0, y: 0 }, delta: Point = { x: 0, y: 0 }): void {
		this.toolsLayer.destroyChildren();

		if (element) {
			const distanceLines: Array<Konva.Line | Konva.Label> = this.canvasDrawerService.createEquipmentDistanceLines({
				element: element,
				beginPoint: this.elementBeginPoint,
				scale: this.elementScale,
				normalize: normalize,
				active: true,
				flip: false
			}, this.planogram.stand, true, delta);

			if (distanceLines.length) this.toolsLayer.add(...distanceLines);
		}

		this.toolsLayer.draw();
	}

	private drawSizes(): void {
		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(this.planogram, this.element, this.elementScale, this.elementBeginPoint);

		this.detailingSizesLayer.destroyChildren();
		this.detailingSizesLayer.add(...this.canvasDrawerService.createPlanogramDetailedSizes({
			element: this.element,
			beginPoint: this.elementBeginPoint,
			normalize: behavior.getOffset(),
			scale: this.elementScale,
			zoom: this.zoom,
			active: false,
			flip: false
		}, this.planogram.stand));
	}

	public dropProduct(event: DndDropEvent): void {
		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(this.planogram, this.element, this.elementScale, this.elementBeginPoint);
		this.activity.dropProduct(this.planogram, behavior, event);
	}

	public changeDetailedElement(detailedElement: Element): void {
		if (!detailedElement) return;
		this.element = detailedElement;
		this.activity.setFocusedCanvas(this.planogram.main_stand, this.element.id, this.canvas.nativeElement);
		this.canvasRender();
	}

	public closeDetailedView(): void {
		this.hostElement.nativeElement.removeEventListener('click', this.onHostClick);
		this.hostElement.nativeElement.removeEventListener('wheel', this.onHostWheel);
		if (this.activity.isCanvasFocused(this.canvas.nativeElement)) this.activity.setFocusedCanvas(null, null, null);
		if (this.widgetRef.close) this.widgetRef.close();
	}

	public changeCanvasListening(listening: boolean) {
		this.detailingLayer.listening(listening);
		this.detailingLayer.batchDraw();
		this.blocksLayer.listening(listening);
		this.blocksLayer.batchDraw();
	}

	private updateDragOutsidePreviews(): void {
		if (!this.stage || !this.canvasContainer?.nativeElement) return;

		this.dragOutsideService.update({
			items: this.activity.activeProducts().map((pg: ProductGroup) => ({
				key: pg,
				canvasProduct: this.canvasDrawerService.getCanvasDetailedProduct(pg)
			})),
			stage: this.stage,
			container: this.canvasContainer.nativeElement,
			overCanvasTargets: Array.from(document.querySelectorAll('detailed-element-widget'))
				.filter((el) => el instanceof HTMLElement) as Array<HTMLElement>
		});
	}

	private clearDragOutsidePreviews(): void {
		this.dragOutsideService.clear();
	}

	private createTooltipSnapshot(
		tooltip: Konva.Label,
		tooltipRect: { x: number; y: number; width: number; height: number }
	): HTMLElement {
		if (!this.canvas?.nativeElement) return null;

		const canvasRect = this.canvas.nativeElement.getBoundingClientRect();
		const snapshot = document.createElement('div');
		const image = document.createElement('img');

		image.src = tooltip.toDataURL({ pixelRatio: window.devicePixelRatio || 1 });
		image.style.width = '100%';
		image.style.height = '100%';

		snapshot.style.position = 'fixed';
		snapshot.style.left = `${canvasRect.left + tooltipRect.x}px`;
		snapshot.style.top = `${canvasRect.top + tooltipRect.y}px`;
		snapshot.style.width = `${tooltipRect.width}px`;
		snapshot.style.height = `${tooltipRect.height}px`;
		snapshot.style.pointerEvents = 'none';
		snapshot.style.userSelect = 'none';
		snapshot.style.zIndex = '9999';

		snapshot.appendChild(image);
		this.hostElement.nativeElement.appendChild(snapshot);

		return snapshot;
	}

	private getCorrectPoint(behavior: PlanogramViewBehavior, pg: ProductGroup, dragOutside: boolean = false): Point {
		let point: Point = null;

		if (dragOutside) {
			point = behavior.convertCanvasPointToPlanogram(this.dragOutsideService.getDropPoint(pg, this.stage));
		}

		if (!point) {
			point = behavior.getPoint(pg, this.canvasDrawerService.getCanvasDetailedProduct(pg));
		}

		return point;
	}

	private handleProductGroupDrop(behavior: PlanogramViewBehavior, stand: IPlanogram, dragOutside: boolean = false): void {
		if (!this.activity.activeProducts().length) {
			this.activity.setProductMove(false);
			this.clearDragOutsidePreviews();
			return;
		}
		this.changeCanvasListening(false);
		const activeProductGroups: Array<Array<ProductGroup>> = behavior.getActiveProductGroups(this.activity.activeProducts());
		const sourceStand = activeProductGroups[0]?.[0]?.element?.stand ?? null;
		const isCrossPlanogram: boolean = !!sourceStand && sourceStand !== stand.stand;
		let sourcePlanogram: IPlanogram | undefined;

		if (isCrossPlanogram) {
			sourcePlanogram = this.activity.getPlanogramByStand(sourceStand);
			if (!sourcePlanogram) return this.dragOutsideService.redrawCanvas$.next();
		}

		for (let productGroup of activeProductGroups) {
			const point: Point = this.getCorrectPoint(behavior, productGroup[0], dragOutside);
			const element: Element = behavior.getAnchorElement(productGroup[0], point);

			if (!element) return this.dragOutsideService.redrawCanvas$.next();

			point.z = behavior.getCorrectZCoordinate(element, point.z, productGroup[0]);
			const result: ICanPutProductsResult = element.canPutProductGroups(productGroup, point);
			if (!result.success) {
				if (result.message) this.snackBarService.setSnackBarNotification(result.message);
				return this.dragOutsideService.redrawCanvas$.next();
			}
		}

		let sourceNormalizeElements: Array<Element> = [];

		if (isCrossPlanogram) {
			sourceNormalizeElements = this.activity.removeProductsFromStandForTransfer(
				sourceStand,
				Utils.to1DArray(activeProductGroups)
			);
			sourceNormalizeElements = this.activity.normalizeElements(sourceStand, Utils.unique(sourceNormalizeElements));
			this.activity.ensureProductsInPlanogramCatalog(stand, Utils.to1DArray(activeProductGroups));
		}

		const normalizeList: Array<Element> = activeProductGroups.map((g: Array<ProductGroup>) => g[0].element);

		for (let productGroup of activeProductGroups) {
			const point: Point = this.getCorrectPoint(behavior, productGroup[0], dragOutside);
			const element: Element = behavior.getAnchorElement(productGroup[0], point);

			if (!element) return this.dragOutsideService.redrawCanvas$.next();

			point.z = behavior.getCorrectZCoordinate(element, point.z, productGroup[0]);
			element.putProductGroups(productGroup, activeProductGroups, { ...point });

			let block = stand.stand.findBlock(
				point.x,
				this.element.getBoundingY().min + this.element.shapes[0].height,
				productGroup[0].Width
			);
			if (this.activity.selectedBlock() !== block) this.activity.setSelectedBlock(block);

			productGroup.forEach((product: ProductGroup) => normalizeList.push(product.element));
		}

		this.planogram.stand.shiftProducts(activeProductGroups, true);

		const changedBlocks: Array<Block> = this.planogram.stand.blocks
			.filter((block: Block) => block.products.some((pg: ProductGroup) => this.activity.isProductActive(pg)));

		if (changedBlocks.length) {
			changedBlocks.forEach((block: Block) => {
				const blockIndex: number = this.planogram.stand.blocks.indexOf(block);
				this.planogram.stand.blocks[blockIndex].updateBlockProducts(
					this.activity.getDependBlockProducts(this.planogram.stand.blocks[blockIndex], this.planogram.stand)
				);
			});
		}

		this.planogram.stand.blocks.forEach((block: Block) => block.clearCache());

		const elements: Array<Element> = this.activity.normalizeElements(this.planogram.stand, Utils.unique(normalizeList));

		const activeElements: Array<ProductGroup> = [...this.activity.activeProducts()];
		activeElements.forEach((pg: ProductGroup, idx: number) => this.activity.setProductActive(pg, !!idx));

		if (this.activity.activeProducts().length > 1 && this.activity.settingsPanel().differentElements) {
			this.activity.closePanel();
		}

		const updatePlanograms: Array<IPlanogram> = isCrossPlanogram && sourcePlanogram
			? [sourcePlanogram, stand]
			: [this.planogram];
		const updateElementIds: Array<string> = isCrossPlanogram
			? Utils.unique([...sourceNormalizeElements, ...elements].map((el: Element) => el.id))
			: elements.map((el: Element) => el.id);

		this.activity.setPlanogram(stand);

		this.activity.requestCanvasUpdate(updatePlanograms, updateElementIds);

		if (isCrossPlanogram && sourcePlanogram) {
			this.activity.historyGenerate$.next(sourcePlanogram.main_stand);
			this.activity.historyGenerate$.next(stand.main_stand);
		} else {
			this.activity.historyGenerate$.next();
		}

		this.activity.setProductMove(false);
		this.changeCanvasListening(true);
		this.clearDragOutsidePreviews();
		this.activity.setFocusedCanvas(this.planogram.main_stand, this.element.id, this.canvas.nativeElement);
	}

	private moveProducts(horizontal: number, vertical: number): void {
		if (!this.activity.editMode() || this.modes.equipmentEditMode()) return;

		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(this.planogram, this.element, this.elementScale, this.elementBeginPoint);
		this.activity.moveProducts(this.planogram, behavior, horizontal, vertical);
	}

	public ngOnDestroy() {
		this.dragAbortController?.abort();
		this.dragAbortController = null;

		this.clearDragOutsidePreviews();
		this.stage.destroy();
		if (this.tinyKeysSubscription) this.tinyKeysSubscription();
	}
}